/**
 * Strategy.js — the page one level up: a kind of deal, added together
 * ====================================================================
 * Every ACE deal as one position, every equity line, every convertible note.
 * "Did the ACE book make money" is a different question from "did this ACE
 * deal make money", and the By strategy pill on the list page is which of the
 * two the page is answering.
 *
 * A group has no currency of its own — an ACE book holds a krona deal beside a
 * dollar one — so the API converts every flow at its own date's fixing and the
 * group is read in dollars or not at all. A deal whose rate is missing is named
 * and left out rather than added at face value. A group's max cash exposure is
 * the deepest point of the group's own curve, never the sum of its deals'
 * troughs: two deals that each tied up a million, one repaid before the other
 * was funded, never tied up two million between them.
 *
 * A deal with no strategy falls into "Untagged". It is not a kind of deal; it
 * is the list of deals somebody has to go and tag on the Deals page.
 */
import React from "react";
import { Badge, Card } from "../../components/ui";
import { ExportCsvButton, InfoTip } from "../../components/PageTools";
import theme from "../../components/theme";
import { day, dealMoney } from "./figures";
import { strategyLabel } from "./listing";
import { StrategyCurve } from "./Curve";
import { Tile } from "./Tiles";

export function StrategyPicker({ groups, selected, onSelect }) {
  return (
    <div className="dr-picker" aria-label="Strategy list">
      {groups.map((group) => {
        const active = group.strategy === selected;
        return (
          <button
            key={group.strategy || "__untagged__"}
            type="button"
            className={`dr-picker-item${active ? " is-active" : ""}`}
            aria-pressed={active}
            onClick={() => onSelect(group.strategy)}
          >
            <span
              className="dr-picker-name"
              style={{ color: group.strategy ? theme.text : theme.textMuted, fontStyle: group.strategy ? "normal" : "italic" }}
            >
              {strategyLabel(group.strategy)}
            </span>
            <span className="dr-picker-meta">
              <span>{dealMoney(group.summary.max_cash_exposure, "USD")} at most</span>
              <span>
                {group.deal_count} {group.deal_count === 1 ? "deal" : "deals"}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StrategyWarnings({ group }) {
  const left = group.deals_not_in_total || [];
  const incomplete = group.deals_with_incomplete_basis || [];
  const untagged = !group.strategy;
  if (!left.length && !incomplete.length && !untagged) return null;
  return (
    <div className="dr-warning" style={{ marginBottom: theme.space.lg }}>
      {untagged && (
        <div style={{ marginBottom: left.length || incomplete.length ? 8 : 0 }}>
          <strong style={{ color: theme.text }}>These deals carry no strategy.</strong> They are in no
          strategy's figures, and this is not a kind of deal — it is the list of deals still to be
          tagged. Set one on the Deals page (Edit → Strategy) and the deal moves into that
          strategy's curve.
        </div>
      )}
      {left.length > 0 && (
        <div style={{ marginBottom: incomplete.length ? 8 : 0 }}>
          <strong style={{ color: theme.text }}>
            {left.length === 1 ? "A deal is" : `${left.length} deals are`} missing from this total.
          </strong>{" "}
          {left.map((d) => d.deal).join(", ")} — an exchange rate is missing for one of their dates,
          so they are left out rather than added at face value.
        </div>
      )}
      {incomplete.length > 0 && (
        <div>
          <strong style={{ color: theme.text }}>Cost basis is incomplete.</strong> Net cash is not a
          return for this strategy yet — uncosted disposals book their whole proceeds as gain, on{" "}
          {incomplete.map((d) => d.deal).join(", ")}.
        </div>
      )}
    </div>
  );
}

// The deals behind the group's figures, each named with its own number, so the
// total can be read back to where it came from. Clicking one opens it.
function StrategyDeals({ group, onOpenDeal }) {
  return (
    <Card
      title="Deals in this strategy"
      subtitle="Each deal's own figures, converted to dollars. Click one to open it."
      action={
        <ExportCsvButton
          filename={`deal-reports-${strategyLabel(group.strategy)}`}
          rows={group.deals}
          columns={[
            { label: "Deal", value: (d) => d.name },
            { label: "Company", value: (d) => d.issuer || d.counterparty || "" },
            { label: "Funded", value: (d) => d.funding_date || "" },
            { label: "Most cash out (USD)", value: (d) => (d.in_total ? d.max_cash_exposure : null) },
            { label: "Cash recovered (USD)", value: (d) => (d.in_total ? d.cash_recovered : null) },
            { label: "Net cash (USD)", value: (d) => (d.in_total ? d.net_cash : null) },
            { label: "Note", value: (d) => (!d.in_total ? "not in the total: an exchange rate is missing" : d.basis_complete ? "" : "provisional") },
          ]}
        />
      }
    >
      <div className="dr-table-scroll">
        <table className="dr-table">
          <thead>
            <tr>
              <th>Deal</th>
              <th className="num">Most cash out</th>
              <th className="num">Cash recovered</th>
              <th className="num">Net cash</th>
              <th aria-label="Notes" />
            </tr>
          </thead>
          <tbody>
            {group.deals.map((d) => (
              <tr key={d.id} onClick={() => onOpenDeal(d.id)} className="dr-row-link">
                <td>
                  <div className="dr-deal-name">{d.name}</div>
                  <div className="dr-deal-sub">
                    {[d.issuer || d.counterparty, d.ticker, d.funding_date && `funded ${day(d.funding_date)}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </td>
                {d.in_total ? (
                  <>
                    <td className="num" style={{ color: theme.danger }}>{dealMoney(d.max_cash_exposure, "USD")}</td>
                    <td className="num" style={{ color: theme.success }}>{dealMoney(d.cash_recovered, "USD")}</td>
                    <td
                      className="num"
                      style={{
                        color: Number(d.net_cash) >= 0 ? theme.success : theme.danger,
                        textDecoration: d.basis_complete ? "none" : "line-through",
                      }}
                    >
                      {dealMoney(d.net_cash, "USD")}
                    </td>
                  </>
                ) : (
                  // A deal left out of the total gets no dollar figure at all.
                  // Its own currency's number is on its own page; putting it in
                  // this column would be the face-value sum this page refuses.
                  <td colSpan={3} className="num" style={{ color: theme.textMuted, fontSize: 12, whiteSpace: "normal" }}>
                    not in the total — no {(d.currency || []).join("/")} rate loaded
                  </td>
                )}
                <td className="num">{!d.basis_complete && <Badge text="provisional" variant="amber" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function StrategyDetail({ group, onOpenDeal }) {
  const s = group.summary;
  const provisional = !s.basis_complete;
  return (
    <>
      <Card
        title={strategyLabel(group.strategy)}
        subtitle={`${group.deal_count} ${group.deal_count === 1 ? "deal" : "deals"} · cumulative cash in USD, converted at each date's fixing`}
        action={
          group.total_is_currency_safe ? (
            <Badge text="USD" variant="gray" />
          ) : (
            <Badge text="Deals missing from this total" variant="yellow" dot />
          )
        }
      >
        <StrategyWarnings group={group} />
        <StrategyCurve group={group} />
      </Card>

      <Card>
        <div className="dr-tiles">
          <Tile
            label="Most cash tied up at once"
            value={dealMoney(s.max_cash_exposure, "USD")}
            tone="negative"
            note={s.max_cash_exposure_date ? `on ${day(s.max_cash_exposure_date)}` : "no cash was deployed"}
            info="The deepest point of this strategy's own curve: the most its deals had out at one time, net of what had come back. Not the sum of each deal's deepest point."
          />
          <Tile
            label="Invested"
            value={dealMoney(s.capital_deployed ?? s.cash_deployed, "USD")}
            note="capital only: not equity-line draws, market buys, or what a same-day sale paid for"
          />
          <Tile
            label="Cash recovered"
            value={dealMoney(s.cash_recovered, "USD")}
            tone="positive"
            provisional={provisional}
            note={provisional ? "understates cost, so overstates recovery" : "gross: everything that came back"}
          />
          <Tile
            label="Net cash"
            value={dealMoney(s.net_cash, "USD")}
            tone={Number(s.net_cash) >= 0 ? "positive" : "negative"}
            provisional={provisional}
            note={provisional ? "not a return until every deal's cost basis is loaded" : `${day(s.first_date)} – ${day(s.last_date)}`}
          />
          <Tile
            label="Deals in these figures"
            value={`${group.deals.filter((d) => d.in_total).length} of ${group.deal_count}`}
            note={`${s.points} days with cash movement`}
            info={
              <>
                A strategy holds deals in more than one currency, so it has no figure of its own at face
                value: every flow is converted at the Riksbank fixing for its own date, and a deal whose
                rate is missing is named above and left out rather than added at par.
              </>
            }
          />
        </div>
      </Card>

      <StrategyDeals group={group} onOpenDeal={onOpenDeal} />
      <div style={{ fontSize: 12, color: theme.textMuted, display: "inline-flex", alignItems: "center", gap: 4 }}>
        Why the most-cash figure is not the sum of the deals'
        <InfoTip>
          Two deals that each tied up a million, one repaid before the other was funded, never tied up
          two million between them. The strategy's figure is the deepest point of its own curve.
        </InfoTip>
      </div>
    </>
  );
}
